import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Grid,
	IconButton,
	InputAdornment,
	Step,
	StepContent,
	StepLabel,
	Tab,
	Tabs,
	TextField,
	Typography,
	withStyles,
} from '@material-ui/core';
import { Field, Form, Formik, FormikActions, FormikProps, FormikValues } from 'formik';
import * as queryString from 'query-string';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';

import {
	AccountLevelEnum,
	AccountStatusEnum,
	ShareSignUpModeEnum,
	SignUpModeEnum,
	SnackBarVariants,
} from '@app/app.enum';
import store from '@app/app.store';
import AccountAppSelectComponent from '@app/components/account/app/account-app-select.component';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomerListComponent from '@app/components/shared/customer-list.component';
import Icon from '@app/components/shared/icon.component';
import environment from '@app/environments/environment';
import * as models from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { StyleRules } from '@material-ui/core/styles';
import ChipInput from 'material-ui-chip-input';

const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);
const isClosed = store.getState().authentication.status === AccountStatusEnum.Closed;
const styles: StyleRules = {
	keyInput: {
		fontSize: 16,
		padding: '10px 14px',
	},
	textLabel: {
		fontSize: 18,
	},
};

const TabPanel = (props: any) => {
	const { children, value, index } = props;

	return (
		<div role='tabpanel' hidden={value !== index}>
			{value === index && <Box>{children}</Box>}
		</div>
	);
};

class CustomerComponent extends React.Component<models.ICustomerProps, models.ICustomerState> {
	private userData: models.IUserSignInResponse;
	private isPartner: boolean;

	private get FormFactory(): models.IAddCustomer {
		return {
			signupUrl: this.state.normalSignUpUrl,
			email: '',
		};
	}

	constructor(props: models.ICustomerProps) {
		super(props);
		this.userData = accountService.getUserData();
		this.isPartner = this.userData.account.level === AccountLevelEnum.Partner;
		const addCustomerSchema = Yup.object().shape<models.IAddCustomer>({
			email: Yup.string()
				.email(intlService.GetFormattedMessage('app.login.signin-email-invalid-validation'))
				.required(intlService.GetFormattedMessage('app.login.signin-email-required-validation')),
			signupUrl: Yup.string(),
		});

		this.state = models.initialCustomerState(
			this.getSignUpUrl(this.userData.apps[0], this.userData, this.isPartner, SignUpModeEnum.Normal),
			this.getSignUpUrl(this.userData.apps[0], this.userData, this.isPartner, SignUpModeEnum.Quick),
			addCustomerSchema
		);
	}

	public componentDidUpdate(prevProps: models.ICustomerProps, prevState: models.ICustomerState) {
		if (prevState.emailChips.length !== this.state.emailChips.length) {
			if (!!this.state.emailChips.length) {
				this.setState({
					addCustomerSchema: Yup.object().shape<models.IAddCustomer>({
						email: Yup.string().email(intlService.GetFormattedMessage('app.login.signin-email-invalid-validation')),
						signupUrl: Yup.string(),
					}),
				});
			} else {
				this.setState({
					addCustomerSchema: Yup.object().shape<models.IAddCustomer>({
						email: Yup.string()
							.email(intlService.GetFormattedMessage('app.login.signin-email-invalid-validation'))
							.required(intlService.GetFormattedMessage('app.login.signin-email-required-validation')),
						signupUrl: Yup.string(),
					}),
				});
			}
		}
	}

	public render() {
		const { classes } = this.props as any;

		return (
			<React.Fragment>
				<Grid container={true} direction='column' spacing={2}>
					{!isClosed ? (
						<Grid item={true} xl={12} lg={12} xs={12}>
							<Accordion>
								<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
									<Typography variant='h6' color='inherit'>
										{this.props.intl.formatMessage({
											id: 'app.api-key.add-customer',
										})}
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									{!this.isPartner ? (
										<AccountAppSelectComponent
											apps={this.userData.apps}
											onAppSelect={(app) => {
												this.setState({
													normalSignUpUrl: this.getSignUpUrl(app, this.userData, this.isPartner, SignUpModeEnum.Normal),
													selectedAppToShare: app,
												});
											}}
										>
											<Step>
												<StepLabel>
													{intlService.GetFormattedMessage('app.customer.share-app') +
														(this.state.selectedAppToShare ? ': ' + this.state.selectedAppToShare.appName : '')}
												</StepLabel>
												<StepContent>{this.getCustomerSignupURLForm(classes)}</StepContent>
											</Step>
										</AccountAppSelectComponent>
									) : (
										this.getCustomerSignupURLForm(classes)
									)}
								</AccordionDetails>
							</Accordion>
						</Grid>
					) : null}
					<Grid item={true} xl={12} lg={12} xs={12}>
						{this.getPHCustomer()}
					</Grid>
				</Grid>
				<div
					id='copyContentHolder'
					style={{
						width: 0,
						height: 0,
						position: 'absolute',
						left: '-99999999px',
					}}
				>
					<textarea style={{ width: 0, height: 0 }} />
				</div>
			</React.Fragment>
		);
	}

	private getPHCustomer = () => <CustomerListComponent history={history} />;

	private copyToClipboard = (textToCopy: string) => {
		const textareaEl = document.getElementById('copyContentHolder').firstChild as HTMLTextAreaElement;
		const snackBarMessage = this.props.intl.formatMessage({
			id: 'app.api-key.copied-to-clipboard',
		});

		textareaEl.value = textToCopy;
		textareaEl.select();
		document.execCommand('copy');

		this.props.openSnackBar(snackBarMessage, SnackBarVariants.Success);
	};

	private sendEmailToCustomer = async (
		addCustomer: models.IAddCustomer,
		formikActions: FormikActions<models.IAddCustomer>
	) => {
		try {
			this.setState({ sendingEmail: true });
			if (!!this.state.emailChips.length) {
				addCustomer.email = this.state.emailChips;
			} else {
				addCustomer.email = [addCustomer.email as string];
			}

			const result = await accountService.SendEmailToCustomer(addCustomer);
			if (result) {
				const snackBarMessage = this.props.intl.formatMessage({
					id: 'app.api-key.email-sent-successfully',
				});
				this.props.openSnackBar(snackBarMessage, SnackBarVariants.Success);
				formikActions.resetForm();
				this.setState({ sendingEmail: false, emailChips: [] });
			}
		} catch (error) {
			formikActions.setValues({
				signupUrl: this.state.normalSignUpUrl,
				email: this.state.emailChips[this.state.emailChips.length - 1],
			});

			this.setState({
				sendingEmail: false,
			});
			this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
		}
	};

	private getSignUpUrl = (
		app: models.IAccountAppResponseVM,
		userData: models.IUserSignInResponse,
		isPartner: boolean,
		signUpMode: SignUpModeEnum
	) => {
		let query: string = '';

		if (signUpMode === SignUpModeEnum.Quick) {
			query = queryString.stringify({
				mode: SignUpModeEnum.Quick,
			});
		}

		if (isPartner) {
			const newQueryString = !!query ? `${query}&` : query;
			query =
				newQueryString +
				queryString.stringify({
					SignupKey: userData.account.id,
				});
		} else {
			const newQueryString = !!query ? `${query}&` : query;
			query =
				newQueryString +
				queryString.stringify({
					partnerAppId: app.appId,
					parentAccountId: userData.account.id,
				});
		}

		return environment.appSetting.baseUrl + 'invitation?' + query;
	};

	private getCustomerSignupURLForm = (classes: any) => (
		<Grid container={true} spacing={3}>
			<Grid item={true} xs={12}>
				<fieldset>
					<legend>
						<Typography variant='body1'>
							{intlService.GetFormattedMessage('app.customer.normal-sign-up-label')}
						</Typography>
					</legend>
					<Typography variant='caption' color='textSecondary'>
						{intlService.GetFormattedMessage('app.customer.normal-sign-up-description-part-I')}
						<br />
						{intlService.GetFormattedMessage('app.customer.normal-sign-up-description-part-II')}
					</Typography>
					<Formik
						initialValues={this.FormFactory}
						onSubmit={this.sendEmailToCustomer}
						validationSchema={this.state.addCustomerSchema}
						enableReinitialize={true}
						render={(props: FormikProps<FormikValues>) => (
							<Form noValidate={true} className='width-100'>
								<Grid container={true} direction='column' spacing={2}>
									<Grid item={true}>
										<Tabs
											value={this.state.normalSignUpTabPanel}
											indicatorColor='primary'
											textColor='primary'
											variant='fullWidth'
											className='mt-1'
											onChange={(e: React.ChangeEvent, newValue: any) =>
												this.setState({ normalSignUpTabPanel: newValue })
											}
										>
											<Tab label={ShareSignUpModeEnum.URL} />
											<Tab label={ShareSignUpModeEnum.HTML} />
											<Tab label={ShareSignUpModeEnum.Email} />
										</Tabs>
										{this.getSharingModeFeature(this.state.normalSignUpTabPanel, props, classes, SignUpModeEnum.Normal)}
									</Grid>
								</Grid>
							</Form>
						)}
					/>
				</fieldset>
			</Grid>
			<Grid item={true} xs={12}>
				<fieldset>
					<legend>
						<Typography variant='body1'>
							{intlService.GetFormattedMessage('app.customer.quick-sign-up-label')}
						</Typography>
					</legend>
					<Typography variant='caption' color='textSecondary'>
						{intlService.GetFormattedMessage('app.customer.quick-sign-up-description-part-I')}
						<br />
						{intlService.GetFormattedMessage('app.customer.quick-sign-up-description-part-II')}
					</Typography>
					<Formik
						initialValues={this.FormFactory}
						onSubmit={this.sendEmailToCustomer}
						validationSchema={this.state.addCustomerSchema}
						enableReinitialize={true}
						render={(props: FormikProps<FormikValues>) => (
							<Form noValidate={true} className='width-100'>
								<Grid container={true} direction='column' spacing={2}>
									<Grid item={true}>
										<Tabs
											value={this.state.quickSignUpTabPanel}
											indicatorColor='primary'
											textColor='primary'
											variant='fullWidth'
											className='mt-1'
											onChange={(e: React.ChangeEvent, newValue: any) =>
												this.setState({ quickSignUpTabPanel: newValue })
											}
										>
											<Tab label={ShareSignUpModeEnum.URL} />
											<Tab label={ShareSignUpModeEnum.HTML} />
										</Tabs>
										{this.getSharingModeFeature(this.state.quickSignUpTabPanel, props, classes, SignUpModeEnum.Quick)}
									</Grid>
								</Grid>
							</Form>
						)}
					/>
				</fieldset>
			</Grid>
		</Grid>
	);

	private getSharingModeFeature = (
		tabPanel: number,
		props: FormikProps<FormikValues>,
		classes: any,
		mode: SignUpModeEnum
	): JSX.Element => {
		const url = mode === SignUpModeEnum.Normal ? props.values.signupUrl : this.state.quickSignUpUrl;

		const tabPanelValue =
			mode === SignUpModeEnum.Normal ? this.state.normalSignUpTabPanel : this.state.quickSignUpTabPanel;

		return (
			<>
				<TabPanel value={tabPanelValue} index={0}>
					<Field
						name='signupUrl'
						component={TextField}
						margin='normal'
						type='text'
						variant='outlined'
						fullWidth={true}
						value={url}
						InputProps={{
							classes: {
								root: 'share-signup-url-panels-margin-top',
								input: classes.keyInput,
							},
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton onClick={() => this.copyToClipboard(url)}>
										<Icon name='file-copy' />
									</IconButton>
								</InputAdornment>
							),
							readOnly: true,
						}}
					/>
				</TabPanel>
				<TabPanel value={tabPanelValue} index={1}>
					<Field
						name='signupUrl'
						component={TextField}
						margin='normal'
						type='text'
						variant='outlined'
						fullWidth={true}
						value={`<iframe style="display:block; width:100%; height:100%;" src="${url}" title="Visible Shipping Portal" frameBorder="0" />`}
						InputProps={{
							classes: {
								root: 'share-signup-url-panels-margin-top',
								input: classes.keyInput,
							},
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										onClick={() =>
											this.copyToClipboard(
												`<iframe style="display:block; width:100%; height:100%;" src="${url}" title="Visible Shipping Portal" frameBorder="0" />`
											)
										}
									>
										<Icon name='file-copy' />
									</IconButton>
								</InputAdornment>
							),
							readOnly: true,
						}}
					/>
				</TabPanel>
				<TabPanel value={tabPanelValue} index={2}>
					<ChipInput
						label='Email'
						margin='normal'
						classes={{
							inputRoot: 'chip-input-padding',
							input: 'input',
							chip: 'chip',
							label: 'email-chip-label',
						}}
						newChipKeys={[' ', ';', ',']}
						newChipKeyCodes={[]}
						blurBehavior='ignore'
						onUpdateInput={(e: React.ChangeEvent<HTMLInputElement>) => {
							props.handleChange('email')(e);
						}}
						onBeforeAdd={() => !props?.errors?.email}
						variant='outlined'
						error={!!props.errors.email}
						helperText={props.errors.email}
						fullWidth={true}
						value={this.state.emailChips}
						onAdd={(chip: string) => {
							props.values.email = '';
							this.setState((prevState: models.ICustomerState) => ({
								emailChips: [...prevState.emailChips, chip],
							}));
						}}
						onDelete={(chip: string) => {
							props.values.email = '';
							this.setState((prevState: models.ICustomerState) => ({
								emailChips: prevState.emailChips.filter((e) => e !== chip),
							}));
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<Button type='submit' variant='text' disabled={this.state.sendingEmail}>
										{this.props.intl.formatMessage({
											id: 'app.customer-send',
										})}
									</Button>
								</InputAdornment>
							),
						}}
					/>
				</TabPanel>
			</>
		);
	};
}

export default withAxiosCancellation(withSnackBar(withStyles(styles)(injectIntl(CustomerComponent))));



// WEBPACK FOOTER //
// ./src/components/customer/customer/customer.component.tsx